import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Clock,
  Send,
  Check,
  Users,
  Sparkles,
  Sunrise,
  Moon,
  Lock,
  Share2,
  Link as LinkIcon,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_ID = 'zoom-times-28-day';

// Option groups — simple labels Jill can tune later
const WEEKDAY_OPTIONS = [
  { id: 'wk-630', label: '6:30 AM', group: 'early' },
  { id: 'wk-645', label: '6:45 AM', group: 'early' },
  { id: 'wk-700', label: '7:00 AM', group: 'early' },
  { id: 'wk-715', label: '7:15 AM', group: 'early' },
  { id: 'wk-730', label: '7:30 AM', group: 'early' },
  { id: 'wk-800', label: '8:00 AM', group: 'early' },
  { id: 'wk-830', label: '8:30 AM', group: 'early' },
  { id: 'wk-midafternoon', label: 'Mid-Afternoon', group: 'general' },
  { id: 'wk-day', label: 'Daytime (any)', group: 'general' },
  { id: 'wk-evening', label: 'Evening', group: 'general' },
];

const FRIDAY_OPTIONS = [
  { id: 'fr-1215', label: '12:15 PM', group: 'general' },
];

const WEEKEND_OPTIONS_SAT = [
  { id: 'sa-700', label: '7:00 AM', group: 'early' },
  { id: 'sa-800', label: '8:00 AM', group: 'early' },
  { id: 'sa-900', label: '9:00 AM', group: 'early' },
];

const WEEKEND_OPTIONS_SUN = [
  { id: 'su-700', label: '7:00 AM', group: 'early' },
  { id: 'su-afternoon', label: 'Afternoon', group: 'general' },
  { id: 'su-evening', label: 'Evening', group: 'general' },
];

const ResultBar = ({ label, count, max }) => {
  const pct = max > 0 ? (count / max) * 100 : 0;
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm font-body">
        <span className="text-stone-700">{label}</span>
        <span className="text-stone-500">{count}</span>
      </div>
      <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className="h-full bg-forest rounded-full"
        />
      </div>
    </div>
  );
};

const ZoomPollPage = () => {
  const [selectedWeekday, setSelectedWeekday] = useState([]);
  const [selectedFriday, setSelectedFriday] = useState([]);
  const [selectedSaturday, setSelectedSaturday] = useState([]);
  const [selectedSunday, setSelectedSunday] = useState([]);
  const [suggestedTimes, setSuggestedTimes] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState(null);
  const [config, setConfig] = useState({ is_closed: false, final_message: '' });
  const [shareStatus, setShareStatus] = useState('');

  const handleShare = async () => {
    const url = typeof window !== 'undefined' ? window.location.href : '';
    const shareData = {
      title: '28 Day Reset · Vote on Zoom Times',
      text: 'Help pick the best times for our 28 Day Reset Zoom calls — vote in 30 seconds:',
      url,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        setShareStatus('Shared!');
      } catch {
        // user canceled — no need to show an error
        return;
      }
    } else if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(url);
        setShareStatus('Link copied!');
      } catch {
        setShareStatus('Copy failed — please copy the URL from your address bar.');
      }
    }
    setTimeout(() => setShareStatus(''), 2500);
  };

  const toggleSelection = (list, setter, label) => {
    setter(list.includes(label) ? list.filter((l) => l !== label) : [...list, label]);
  };

  const fetchResults = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/zoom-poll/results?poll_id=${POLL_ID}`);
      if (res.ok) setResults(await res.json());
    } catch (err) {
      console.error('Failed to fetch poll results:', err);
    }
  }, []);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/zoom-poll/config?poll_id=${POLL_ID}`);
      if (res.ok) setConfig(await res.json());
    } catch (err) {
      console.error('Failed to fetch poll config:', err);
    }
  }, []);

  useEffect(() => {
    fetchResults();
    fetchConfig();
  }, [fetchResults, fetchConfig]);

  // Per-page title + meta description (browser tab + some social crawlers)
  useEffect(() => {
    const prevTitle = document.title;
    document.title = '28 Day Reset · Zoom Meet-Up Preferred Time Votes · Grounded Wellness with Jill';
    const descEl = document.querySelector('meta[name="description"]');
    const prevDesc = descEl?.getAttribute('content');
    if (descEl) {
      descEl.setAttribute(
        'content',
        "Help pick the best times for live Zoom calls during the 28 Day Reset. Vote on weekday and weekend slots — one quick poll."
      );
    }
    const ogTitle = document.querySelector('meta[property="og:title"]');
    const prevOgTitle = ogTitle?.getAttribute('content');
    ogTitle?.setAttribute('content', '28 Day Reset · Zoom Meet-Up Preferred Time Votes');
    const ogDesc = document.querySelector('meta[property="og:description"]');
    const prevOgDesc = ogDesc?.getAttribute('content');
    ogDesc?.setAttribute(
      'content',
      "Help pick the best times for live Zoom calls during the 28 Day Reset. Cast your vote in 30 seconds."
    );
    return () => {
      document.title = prevTitle;
      if (descEl && prevDesc != null) descEl.setAttribute('content', prevDesc);
      if (ogTitle && prevOgTitle != null) ogTitle.setAttribute('content', prevOgTitle);
      if (ogDesc && prevOgDesc != null) ogDesc.setAttribute('content', prevOgDesc);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const totalPicks = selectedWeekday.length + selectedFriday.length + selectedSaturday.length + selectedSunday.length;
    if (totalPicks === 0 && !suggestedTimes.trim()) {
      setError('Please pick at least one time or suggest one in the comments below.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/zoom-poll/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          poll_id: POLL_ID,
          name,
          email,
          weekday_times: selectedWeekday,
          friday_times: selectedFriday,
          saturday_times: selectedSaturday,
          sunday_times: selectedSunday,
          suggested_times: suggestedTimes,
          notes,
        }),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b.detail || 'Something went wrong.');
      }
      setSubmitted(true);
      fetchResults();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const totalVotes = results?.total_votes ?? 0;
  const maxWeekday = Math.max(1, ...Object.values(results?.weekday_counts || {}));
  const maxFriday = Math.max(1, ...Object.values(results?.friday_counts || {}));
  const maxSat = Math.max(1, ...Object.values(results?.saturday_counts || {}));
  const maxSun = Math.max(1, ...Object.values(results?.sunday_counts || {}));

  const renderOption = (opt, selectedList, toggleFn) => {
    const selected = selectedList.includes(opt.label);
    const Icon = opt.group === 'early' ? Sunrise : Moon;
    return (
      <button
        key={opt.id}
        type="button"
        onClick={() => toggleFn(opt.label)}
        className={`flex items-center gap-2 px-4 py-3 rounded-xl border-2 transition-all font-body text-sm text-left ${
          selected
            ? 'bg-forest text-cream border-forest shadow-sm'
            : 'bg-white text-stone-700 border-stone-200 hover:border-forest/60'
        }`}
        data-testid={`poll-option-${opt.id}`}
      >
        <Icon size={14} className={selected ? 'text-cream/80' : 'text-stone-400'} />
        {opt.label}
        {selected && <Check size={14} className="ml-auto" />}
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-cream py-12 px-4" data-testid="zoom-poll-page">
      <div className="max-w-3xl mx-auto">
        <Link
          to="/programs"
          className="inline-flex items-center gap-2 text-forest/70 hover:text-forest font-body text-sm mb-8"
        >
          <ArrowLeft size={16} /> Back to Programs
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={16} className="text-[#8a6a5a]" />
            <p className="text-xs font-body text-[#8a6a5a] uppercase tracking-[0.25em]">
              28 Day Reset · Zoom Meet-Up
            </p>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl text-forest mb-3">
            Preferred Time Votes
          </h1>
          <p className="text-stone-600 font-body leading-relaxed max-w-2xl">
            {config.is_closed
              ? "Thanks to everyone who voted — the schedule is set. See the final times above and full breakdown below."
              : "Group Zoom times are scheduled for early mornings — roughly 7:00 AM Monday–Thursday and 12:15 PM Friday. We have a little flexibility as the program unfolds. We'd love to know what are the most ideal Zoom times for you to meet as a group — pick as many as work for you, or suggest a different slot in the comments."}
          </p>
          {results && (
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <div className="inline-flex items-center gap-2 text-xs font-body text-stone-500 bg-white/60 border border-stone-200 rounded-full px-3 py-1">
                <Users size={12} />
                {totalVotes} {totalVotes === 1 ? 'vote' : 'votes'} so far
              </div>
              <button
                type="button"
                onClick={handleShare}
                className="inline-flex items-center gap-2 text-xs font-body text-forest hover:text-forest-dark bg-white border border-stone-300 hover:border-forest rounded-full px-3 py-1 transition-colors"
                data-testid="poll-share-button"
              >
                {navigator.share ? <Share2 size={12} /> : <LinkIcon size={12} />}
                {shareStatus || (navigator.share ? 'Share this poll' : 'Copy link')}
                {shareStatus && <Check size={12} />}
              </button>
            </div>
          )}
        </motion.div>

        {/* Poll Closed Banner */}
        {config.is_closed && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 bg-forest text-cream rounded-2xl p-6 md:p-8"
            data-testid="poll-closed-banner"
          >
            <div className="flex items-start gap-4">
              <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-cream/15 flex-shrink-0">
                <Lock size={18} />
              </div>
              <div>
                <p className="text-xs font-body text-[#d9c9a3] uppercase tracking-[0.25em] mb-2">
                  Poll Closed · Thank You!
                </p>
                <h2 className="font-heading text-2xl md:text-3xl leading-tight mb-3">
                  {config.final_message
                    ? 'Final Class Times'
                    : 'Voting has closed'}
                </h2>
                {config.final_message && (
                  <p className="font-body text-cream/90 leading-relaxed whitespace-pre-wrap">
                    {config.final_message}
                  </p>
                )}
                <p className="font-body text-xs text-cream/60 mt-4">
                  Scroll down to see what the group picked.
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* FORM */}
        {!submitted && !config.is_closed && (
          <form onSubmit={handleSubmit} className="space-y-8" noValidate>
            {/* Monday–Thursday */}
            <section
              className="bg-white border border-stone-200 rounded-2xl p-6 md:p-8"
              data-testid="poll-weekday-section"
            >
              <div className="flex items-center gap-2 mb-1">
                <Clock size={16} className="text-forest" />
                <h2 className="font-heading text-xl text-forest">Monday – Thursday</h2>
              </div>
              <p className="text-xs text-stone-500 font-body mb-5">
                Pick anything that works — morning slots + general windows below.
              </p>
              <p className="text-[10px] font-body text-stone-400 uppercase tracking-[0.2em] mb-2">
                Morning
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-5">
                {WEEKDAY_OPTIONS.filter((o) => o.group === 'early').map((o) =>
                  renderOption(o, selectedWeekday, (l) =>
                    toggleSelection(selectedWeekday, setSelectedWeekday, l)
                  )
                )}
              </div>
              <p className="text-[10px] font-body text-stone-400 uppercase tracking-[0.2em] mb-2">
                Other Times of Day
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {WEEKDAY_OPTIONS.filter((o) => o.group === 'general').map((o) =>
                  renderOption(o, selectedWeekday, (l) =>
                    toggleSelection(selectedWeekday, setSelectedWeekday, l)
                  )
                )}
              </div>
            </section>

            {/* Friday */}
            <section
              className="bg-white border border-stone-200 rounded-2xl p-6 md:p-8"
              data-testid="poll-friday-section"
            >
              <div className="flex items-center gap-2 mb-1">
                <Clock size={16} className="text-[#4a7a7a]" />
                <h2 className="font-heading text-xl text-forest">Friday</h2>
              </div>
              <p className="text-xs text-stone-500 font-body mb-5">
                One option that aligns with our regular Friday flow.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {FRIDAY_OPTIONS.map((o) =>
                  renderOption(o, selectedFriday, (l) =>
                    toggleSelection(selectedFriday, setSelectedFriday, l)
                  )
                )}
              </div>
            </section>

            {/* Saturday */}
            <section
              className="bg-white border border-stone-200 rounded-2xl p-6 md:p-8"
              data-testid="poll-saturday-section"
            >
              <div className="flex items-center gap-2 mb-1">
                <Clock size={16} className="text-[#8a6a5a]" />
                <h2 className="font-heading text-xl text-forest">Saturday</h2>
              </div>
              <p className="text-xs text-stone-500 font-body mb-5">
                Morning options for Saturday.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {WEEKEND_OPTIONS_SAT.map((o) =>
                  renderOption(o, selectedSaturday, (l) =>
                    toggleSelection(selectedSaturday, setSelectedSaturday, l)
                  )
                )}
              </div>
            </section>

            {/* Sunday */}
            <section
              className="bg-white border border-stone-200 rounded-2xl p-6 md:p-8"
              data-testid="poll-sunday-section"
            >
              <div className="flex items-center gap-2 mb-1">
                <Clock size={16} className="text-[#6a5a78]" />
                <h2 className="font-heading text-xl text-forest">Sunday</h2>
              </div>
              <p className="text-xs text-stone-500 font-body mb-5">
                Slower, more flexible options.
              </p>
              <p className="text-[10px] font-body text-stone-400 uppercase tracking-[0.2em] mb-2">
                Morning
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-5">
                {WEEKEND_OPTIONS_SUN.filter((o) => o.group === 'early').map((o) =>
                  renderOption(o, selectedSunday, (l) =>
                    toggleSelection(selectedSunday, setSelectedSunday, l)
                  )
                )}
              </div>
              <p className="text-[10px] font-body text-stone-400 uppercase tracking-[0.2em] mb-2">
                Other Times of Day
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {WEEKEND_OPTIONS_SUN.filter((o) => o.group === 'general').map((o) =>
                  renderOption(o, selectedSunday, (l) =>
                    toggleSelection(selectedSunday, setSelectedSunday, l)
                  )
                )}
              </div>
            </section>

            {/* Suggested Times (freeform) */}
            <section
              className="bg-white border border-stone-200 rounded-2xl p-6 md:p-8"
              data-testid="poll-suggested-section"
            >
              <div className="flex items-center gap-2 mb-1">
                <Sparkles size={16} className="text-[#8a6a5a]" />
                <h2 className="font-heading text-xl text-forest">Don't see your time?</h2>
              </div>
              <p className="text-xs text-stone-500 font-body mb-4">
                Suggest a day &amp; time that would work better for you — anything goes.
              </p>
              <textarea
                rows={3}
                placeholder="e.g. Tuesday 6:00 AM, Thursday lunchtime, Sunday 6 PM…"
                value={suggestedTimes}
                onChange={(e) => setSuggestedTimes(e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg border border-stone-300 font-body text-sm focus:outline-none focus:border-forest resize-none"
                data-testid="poll-suggested-times"
              />
            </section>

            {/* About you (optional) */}
            <section className="bg-white border border-stone-200 rounded-2xl p-6 md:p-8">
              <h2 className="font-heading text-xl text-forest mb-1">A little about you</h2>
              <p className="text-xs text-stone-500 font-body mb-4">
                Optional — but helps Jill follow up and lets you update your answer later.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <input
                  type="text"
                  placeholder="Name (optional)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg border border-stone-300 font-body text-sm focus:outline-none focus:border-forest"
                  data-testid="poll-name"
                />
                <input
                  type="email"
                  placeholder="Email (optional)"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg border border-stone-300 font-body text-sm focus:outline-none focus:border-forest"
                  data-testid="poll-email"
                />
              </div>
              <textarea
                rows={3}
                placeholder="Anything else Jill should know? (time zone, preferences, etc.)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg border border-stone-300 font-body text-sm focus:outline-none focus:border-forest resize-none"
                data-testid="poll-notes"
              />
            </section>

            {error && (
              <p className="text-sm text-terracotta font-body" data-testid="poll-error">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 bg-forest text-cream font-body font-medium px-6 py-3 rounded-full hover:bg-forest-dark transition-colors disabled:opacity-60"
              data-testid="poll-submit"
            >
              {submitting ? 'Sending your vote…' : (<><Send size={14} /> Submit My Preferences</>)}
            </button>
          </form>
        )}

        {/* THANK YOU + RESULTS */}
        {submitted && (
          <div
            className="bg-forest/5 border border-forest/20 rounded-2xl p-8 text-center mb-8"
            data-testid="poll-success"
          >
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-forest text-cream mb-3">
              <Check size={22} />
            </div>
            <h2 className="font-heading text-2xl text-forest mb-2">Thanks for weighing in{name ? `, ${name.split(' ')[0]}` : ''}!</h2>
            <p className="text-stone-600 font-body text-sm">
              Here's what the group is saying so far 👇
            </p>
          </div>
        )}

        {/* LIVE RESULTS */}
        {results && (submitted || totalVotes > 0 || config.is_closed) && (
          <motion.section
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-stone-200 rounded-2xl p-6 md:p-8 mt-10"
            data-testid="poll-results"
          >
            <div className="flex items-center gap-2 mb-6">
              <Users size={16} className="text-forest" />
              <h2 className="font-heading text-xl text-forest">
                {config.is_closed ? 'Final Results' : 'Live Results'}
              </h2>
              <span className="text-xs font-body text-stone-500 ml-auto">
                {totalVotes} total {totalVotes === 1 ? 'vote' : 'votes'}
              </span>
            </div>

            <h3 className="font-heading text-sm text-[#8a6a5a] uppercase tracking-[0.2em] mb-4">
              Monday – Thursday
            </h3>
            <div className="space-y-3 mb-8">
              {WEEKDAY_OPTIONS.map((o) => (
                <ResultBar
                  key={o.id}
                  label={o.label}
                  count={results.weekday_counts?.[o.label] || 0}
                  max={maxWeekday}
                />
              ))}
            </div>

            <h3 className="font-heading text-sm text-[#8a6a5a] uppercase tracking-[0.2em] mb-4">
              Friday
            </h3>
            <div className="space-y-3 mb-8">
              {FRIDAY_OPTIONS.map((o) => (
                <ResultBar
                  key={o.id}
                  label={o.label}
                  count={results.friday_counts?.[o.label] || 0}
                  max={maxFriday}
                />
              ))}
            </div>

            <h3 className="font-heading text-sm text-[#8a6a5a] uppercase tracking-[0.2em] mb-4">
              Saturday
            </h3>
            <div className="space-y-3 mb-8">
              {WEEKEND_OPTIONS_SAT.map((o) => (
                <ResultBar
                  key={o.id}
                  label={o.label}
                  count={results.saturday_counts?.[o.label] || 0}
                  max={maxSat}
                />
              ))}
            </div>

            <h3 className="font-heading text-sm text-[#8a6a5a] uppercase tracking-[0.2em] mb-4">
              Sunday
            </h3>
            <div className="space-y-3">
              {WEEKEND_OPTIONS_SUN.map((o) => (
                <ResultBar
                  key={o.id}
                  label={o.label}
                  count={results.sunday_counts?.[o.label] || 0}
                  max={maxSun}
                />
              ))}
            </div>
          </motion.section>
        )}
      </div>
    </div>
  );
};

export default ZoomPollPage;
